import React from 'react';
import { FieldDate, FieldSelect } from 'components/atoms';
import FormControl from 'components/atoms/form-control';
import Input from 'components/atoms/field-input';
import { useFormContext, Controller } from 'react-hook-form';
import { GridFormContainer } from '../../ThirdPartyFormModal.styles';
import dayjs from 'dayjs';
import { useCountryHelper } from 'helpers/countriesHelper';
import { ThirdPartyDataConfigDictionary } from 'services/getThirdPartyConfiguration';
import { Gender, ThirdPartyType } from 'types';

const AdditionalInfo: React.FC<{
  thirdPartyDataConfig: ThirdPartyDataConfigDictionary;
}> = ({ thirdPartyDataConfig }) => {
  const {
    control,
    register,
    formState: { errors, touchedFields },
    watch,
  } = useFormContext();

  const formDataType = watch('type');

  const isIndividual = formDataType === ThirdPartyType.Individual;
  const isBusiness = formDataType === ThirdPartyType.Business;

  const { sortedCountries: countries } = useCountryHelper();

  const genderOptions = Object.keys(Gender).map((key) => ({
    name: key,
    value: Gender[key],
  }));

  const isValid = (field: string) => {
    return !errors?.[field]?.message && !touchedFields?.[field]?.message;
  };

  return (
    <>
      <GridFormContainer>
        <>
          <FormControl errorMessage={errors?.addressLineOne?.message}>
            <Input
              label="Address"
              id="addressLineOne"
              name="addressLineOne"
              register={register}
              fullWidth
              valid={isValid('addressLineOne')}
              required={thirdPartyDataConfig.addressLineOne?.isMandatory}
            />
          </FormControl>
          <FormControl errorMessage={errors?.city?.message}>
            <Input
              label="City"
              id="city"
              name="city"
              register={register}
              fullWidth
              valid={isValid('city')}
              required={thirdPartyDataConfig.city?.isMandatory}
            />
          </FormControl>
          <FormControl errorMessage={errors?.county?.message}>
            <Input
              label="State/region/province"
              id="county"
              name="county"
              register={register}
              fullWidth
              valid={isValid('county')}
              required={thirdPartyDataConfig.county?.isMandatory}
            />
          </FormControl>
          <FormControl errorMessage={errors?.zipCode?.message}>
            <Input
              label="Postcode/Zip Code"
              id="zipCode"
              name="zipCode"
              register={register}
              fullWidth
              valid={isValid('zipCode')}
              required={thirdPartyDataConfig.zipCode?.isMandatory}
            />
          </FormControl>
          <FormControl errorMessage={errors?.countryTwo?.message}>
            <FieldSelect
              id="countryTwo"
              placeholder="Please Select"
              placeholderValue="DEFAULT"
              label="Country 2"
              options={countries}
              register={register}
              fullWidth
              valid={isValid('countryTwo')}
              required={thirdPartyDataConfig.countryTwo?.isMandatory}
            ></FieldSelect>
          </FormControl>
          <FormControl errorMessage={errors?.countryThree?.message}>
            <FieldSelect
              id="countryThree"
              placeholder="Please Select"
              placeholderValue="DEFAULT"
              label="Country 3"
              options={countries}
              register={register}
              fullWidth
              valid={isValid('countryThree')}
              required={thirdPartyDataConfig.countryThree?.isMandatory}
            ></FieldSelect>
          </FormControl>
        </>
        {isIndividual && (
          <>
            <FormControl errorMessage={errors.gender?.message}>
              <FieldSelect
                valid={!errors?.gender?.message}
                id="gender"
                placeholder="Please Select"
                placeholderValue="DEFAULT"
                label="Gender"
                options={genderOptions}
                register={register}
                fullWidth
                required={thirdPartyDataConfig.gender?.isMandatory}
              ></FieldSelect>
            </FormControl>
            <FormControl errorMessage={errors.dateOfBirth?.message}>
              <Controller
                control={control}
                name="dateOfBirth"
                render={({ field }) => (
                  <FieldDate
                    fullWidth={true}
                    valid={!errors.dateOfBirth?.message}
                    label="Date of birth"
                    value={field.value ? dayjs(field.value, 'DD/MM/YYYY').format('DD/MM/YYYY') : ''}
                    onChange={(date) => {
                      field.onChange(
                        date
                          ? dayjs(new Date(date.year, date.month.index, date.day)).format(
                              'DD/MM/YYYY'
                            )
                          : null
                      );
                    }}
                    format="DD/MM/YYYY"
                    required={thirdPartyDataConfig.dateOfBirth?.isMandatory}
                  />
                )}
              />
            </FormControl>
            <FormControl>
              <FieldSelect
                label="Nationality"
                id="nationality"
                register={register}
                options={countries}
                placeholder="Please Select"
                placeholderValue="DEFAULT"
                fullWidth
                valid={isValid('nationality')}
                required={thirdPartyDataConfig.nationality.isMandatory}
              />
            </FormControl>
            <FormControl errorMessage={errors.passportNumber?.message}>
              <Input
                label="Passport number"
                id="passportNumber"
                name="passportNumber"
                register={register}
                fullWidth
                valid={isValid('passportNumber')}
                required={thirdPartyDataConfig.passportNumber?.isMandatory}
              />
            </FormControl>
            <FormControl errorMessage={errors.nationalIDNumber?.message}>
              <Input
                label="National ID number"
                id="nationalIDNumber"
                name="nationalIDNumber"
                register={register}
                fullWidth
                valid={isValid('nationalIDNumber')}
                required={thirdPartyDataConfig.nationalIDNumber?.isMandatory}
              />
            </FormControl>
            <FormControl errorMessage={errors.otherIDNumber?.message}>
              <Input
                label="Other ID number"
                id="otherIDNumber"
                name="otherIDNumber"
                register={register}
                fullWidth
                valid={isValid('otherIDNumber')}
                required={thirdPartyDataConfig.otherIDNumber?.isMandatory}
              />
            </FormControl>
          </>
        )}
        {isBusiness && (
          <>
            <FormControl errorMessage={errors.registrationNumber?.message}>
              <Input
                label="Registration number"
                id="registrationNumber"
                name="registrationNumber"
                register={register}
                fullWidth
                valid={isValid('registrationNumber')}
                required={thirdPartyDataConfig.registrationNumber?.isMandatory}
              />
            </FormControl>
            <FormControl errorMessage={errors.swift?.message}>
              <Input
                label="SWIFT BIC number"
                id="swift"
                name="swift"
                register={register}
                fullWidth
                valid={isValid('swift')}
                required={thirdPartyDataConfig.swift?.isMandatory}
              />
            </FormControl>
            <FormControl errorMessage={errors?.companyUrl?.message}>
              <Input
                label="Company website"
                id="companyUrl"
                name="companyUrl"
                register={register}
                fullWidth
                valid={isValid('companyUrl')}
                required={thirdPartyDataConfig.companyUrl?.isMandatory}
              />
            </FormControl>
          </>
        )}
      </GridFormContainer>
    </>
  );
};

export default AdditionalInfo;
